import { useCallback, useEffect, useRef } from 'react';

import { useLocation, useNavigate } from 'react-router-dom';

import { useMenu } from 'global/MenuContext';
import { useMenuSelection } from 'global/MenuSelectionContext';
import { useMenuRoutes } from 'hooks/useMenuRoutes';
import { isMeetingRoomExtras } from 'types/menuTypes';
import { logger } from 'utils/logger';
import { extractDocumentId, findMenuPathByDocumentId, findMenuPathByUrl } from 'utils/menuUtils';

interface NavigationState {
  path: string;
  documentId: string;
  level: 'first' | 'second' | 'third';
  timestamp: number;
}

const NAVIGATION_STORAGE_KEY = 'app_navigation_state';
const RECENT_MENUS_KEY = 'recent_menus';

export const useNavigationSync = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { menu } = useMenu();
  const { selectMenuByDocumentId, currentLevel, activeDocumentId } = useMenuSelection();
  const menuRoutes = useMenuRoutes();
  const isNavigating = useRef(false);
  const lastProcessedPath = useRef<string>('');

  // Function to save navigation state
  const saveNavigationState = useCallback((state: NavigationState) => {
    try {
      localStorage.setItem(NAVIGATION_STORAGE_KEY, JSON.stringify(state));

      // Also save to recent menus
      const recentMenus = JSON.parse(localStorage.getItem(RECENT_MENUS_KEY) || '[]');
      const existingIndex = recentMenus.findIndex((item: any) => item.path === state.path);

      if (existingIndex > -1) {
        recentMenus.splice(existingIndex, 1);
      }

      recentMenus.unshift({
        path: state.path,
        documentId: state.documentId,
        level: state.level,
        timestamp: state.timestamp,
      });

      // Keep only the 10 most recent
      if (recentMenus.length > 10) {
        recentMenus.splice(10);
      }

      localStorage.setItem(RECENT_MENUS_KEY, JSON.stringify(recentMenus));
    } catch (error) {
      logger.error('[useNavigationSync] Error saving state:', error);
    }
  }, []);

  // Function to load navigation state
  const loadNavigationState = useCallback((): NavigationState | null => {
    try {
      const stored = localStorage.getItem(NAVIGATION_STORAGE_KEY);
      if (!stored) return null;

      const state: NavigationState = JSON.parse(stored);
      const now = Date.now();

      // Check if the state is not too old (5 minutes)
      if (now - state.timestamp > 5 * 60 * 1000) {
        localStorage.removeItem(NAVIGATION_STORAGE_KEY);
        return null;
      }

      return state;
    } catch (error) {
      logger.error('[useNavigationSync] Error loading state:', error);
      localStorage.removeItem(NAVIGATION_STORAGE_KEY);
      return null;
    }
  }, []);

  // Function to find a smart fallback route
  const findSmartFallbackLocal = useCallback(() => {
    const currentPath = decodeURIComponent(window.location.pathname);

    logger.debug('[useNavigationSync] Looking for smart fallback for:', {
      data: currentPath,
    });

    // 1. Try to find a menu similar to the current one
    const currentMenuPath = findMenuPathByUrl(menu, currentPath);
    if (currentMenuPath?.items.length) {
      const parentMenu = currentMenuPath.items[0];
      if (parentMenu.subMenu?.length) {
        const firstSubMenu = parentMenu.subMenu[0];
        const subMenuPath = findMenuPathByDocumentId(
          menu,
          firstSubMenu.feature?.documentId ||
            (firstSubMenu.extras && isMeetingRoomExtras(firstSubMenu.extras)
              ? firstSubMenu.extras.internal_id
              : '') ||
            '',
          firstSubMenu.path
        );
        if (subMenuPath) {
          logger.debug('[useNavigationSync] Fallback 1: Submenu of current menu:', {
            data: subMenuPath.path,
          });
          return {
            path: subMenuPath.path,
            documentId: subMenuPath.documentId,
            reason: 'current_submenu',
          };
        }
      }
    }

    // 2. Try to find a recently used active menu
    try {
      const recentMenus = JSON.parse(localStorage.getItem(RECENT_MENUS_KEY) || '[]');
      if (recentMenus.length > 0) {
        for (const recentMenu of recentMenus) {
          const menuPath = findMenuPathByUrl(menu, recentMenu.path);
          if (menuPath?.items.length) {
            logger.debug('[useNavigationSync] Fallback 2: Recent menu:', {
              data: recentMenu.path,
            });
            return {
              path: recentMenu.path,
              documentId: recentMenu.documentId,
              reason: 'recent_menu',
            };
          }
        }
      }
    } catch (error) {
      logger.error('[useNavigationSync] Error processing recent menus:', error);
    }

    // 3. Try to find an active menu at the same level
    const currentLevelNumber = currentLevel === 'first' ? 1 : currentLevel === 'second' ? 2 : 3;
    const menusAtSameLevel = menu.filter(item => {
      if (currentLevelNumber === 1) return item.level === 'first' && item.isActive;
      if (currentLevelNumber === 2) return item.level === 'second' && item.isActive;
      return item.level === 'third' && item.isActive;
    });

    if (menusAtSameLevel.length > 0) {
      const firstMenu = menusAtSameLevel[0];
      const menuPath = findMenuPathByDocumentId(
        menu,
        firstMenu.feature?.documentId ||
          (firstMenu.extras && isMeetingRoomExtras(firstMenu.extras)
            ? firstMenu.extras.internal_id
            : '') ||
          ''
      );
      if (menuPath) {
        logger.debug('[useNavigationSync] Fallback 3: Menu at the same level:', {
          data: menuPath.path,
        });
        return {
          path: menuPath.path,
          documentId: menuPath.documentId,
          reason: 'same_level',
        };
      }
    }

    // 4. Last resort: first active menu
    const firstActiveMenu = menu.find(item => item.isActive);
    if (firstActiveMenu?.subMenu?.length) {
      const firstSubMenu = firstActiveMenu.subMenu[0];
      const menuPath = findMenuPathByDocumentId(
        menu,
        firstSubMenu.feature?.documentId ||
          (firstSubMenu.extras && isMeetingRoomExtras(firstSubMenu.extras)
            ? firstSubMenu.extras.internal_id
            : '') ||
          ''
      );
      if (menuPath) {
        logger.debug('[useNavigationSync] Fallback 4: First active menu:', {
          data: menuPath.path,
        });
        return {
          path: menuPath.path,
          documentId: menuPath.documentId,
          reason: 'first_active',
        };
      }
    }

    logger.warn('[useNavigationSync] No fallback found');
    return null;
  }, [findMenuPathByUrl, menu, currentLevel]);

  // Function to navigate robustly
  const navigateToPath = useCallback(
    (path: string, documentId?: string) => {
      if (isNavigating.current) return;

      isNavigating.current = true;

      try {
        logger.debug('[useNavigationSync] Navigating to:', { path, documentId });

        // If we have documentId, use selectMenuByDocumentId
        if (documentId) {
          selectMenuByDocumentId(documentId, true, undefined, path);
        } else {
          // Otherwise, just navigate
          navigate(path);
        }

        // Save the navigation state
        const menuPath = findMenuPathByUrl(menu, path);
        if (menuPath?.items.length) {
          const lastMenu = menuPath.items[menuPath.items.length - 1];
          const level = menuPath.level;

          saveNavigationState({
            path,
            documentId: documentId || menuPath.documentId,
            level,
            timestamp: Date.now(),
          });
        }
      } catch (error) {
        logger.error('[useNavigationSync] Navigation error:', error);
      } finally {
        // Reset the flag after a short delay
        setTimeout(() => {
          isNavigating.current = false;
        }, 100);
      }
    },
    [navigate, selectMenuByDocumentId, findMenuPathByUrl, saveNavigationState, menu]
  );

  // Function to restore navigation
  const restoreNavigation = useCallback(() => {
    const savedState = loadNavigationState();
    if (!savedState) return false;

    // Check if the route is still valid
    const isValidRoute = menuRoutes.some(route => route.path === savedState.path);
    if (!isValidRoute) {
      logger.debug('[useNavigationSync] Saved route is no longer valid:', { data: savedState.path });
      localStorage.removeItem(NAVIGATION_STORAGE_KEY);
      return false;
    }

    logger.debug('[useNavigationSync] Restoring navigation:', { data: savedState });
    navigateToPath(savedState.path, savedState.documentId);
    return true;
  }, [loadNavigationState, menuRoutes, navigateToPath]);

  // Automatic synchronization based on URL
  useEffect(() => {
    if (isNavigating.current || location.pathname === lastProcessedPath.current) {
      return;
    }

    const currentPath = decodeURIComponent(location.pathname);
    lastProcessedPath.current = currentPath;

    // If we are on a public route, do not sync
    const publicRoutes = ['/login', '/register', '/forgot-password', '/register-iframe'];
    const isEmbeddedFormPath = currentPath.startsWith('/form/');
    if (publicRoutes.includes(currentPath) || isEmbeddedFormPath) {
      return;
    }

    // Find the menu corresponding to the current path
    const menuPath = findMenuPathByUrl(menu, currentPath);
    if (!menuPath?.items.length) {
      logger.debug('[useNavigationSync] Menu not found for path:', { data: currentPath });
      return;
    }

    const lastMenu = menuPath.items[menuPath.items.length - 1];
    const documentId = extractDocumentId(lastMenu);

    if (documentId) {
      // Prevent restore navigation from running during sync
      isNavigating.current = true;

      logger.debug('[useNavigationSync] Syncing with menu:', {
        path: currentPath,
        documentId,
        level: menuPath.level,
      });

      // Use selectMenuByDocumentId without navigating (we are already on the correct URL)
      selectMenuByDocumentId(documentId, false, menuPath.items, currentPath);

      // Reset after a short delay to allow state updates
      setTimeout(() => {
        isNavigating.current = false;
      }, 150);
    }
  }, [location.pathname, findMenuPathByUrl, selectMenuByDocumentId, menu]);

  // Restore navigation when the menu is loaded
  useEffect(() => {
    // Skip if navigation/sync is in progress
    if (isNavigating.current) {
      return;
    }

    if (menu.length > 0 && !activeDocumentId) {
      const currentPath = decodeURIComponent(window.location.pathname);

      // Exclude event-invite paths and embedded form paths from automatic navigation
      const isEventInvitePath = currentPath.startsWith('/invite/event/');
      const isEmbeddedFormPath = currentPath.startsWith('/form/');
      if (isEventInvitePath || isEmbeddedFormPath) {
        logger.debug('[useNavigationSync] Special path detected, skipping auto-navigation:', {
          isEventInvitePath,
          isEmbeddedFormPath,
          currentPath,
        });
        return;
      }

      // FIRST: Check if the current route is valid and found in the menu
      const currentMenuPath = findMenuPathByUrl(menu, currentPath);
      if (currentMenuPath?.items.length) {
        logger.debug(
          '[useNavigationSync] Current route found in menu, awaiting automatic sync'
        );
        return;
      }

      // SECOND: Check if the current route is valid in menuRoutes
      const isCurrentRouteValid =
        menuRoutes.length > 0 && menuRoutes.some(route => route.path === currentPath);
      if (isCurrentRouteValid) {
        logger.debug(
          '[useNavigationSync] Current route is valid in menu, awaiting automatic sync'
        );
        return;
      }

      // THIRD: Try to restore from localStorage
      const restored = restoreNavigation();
      if (!restored) {
        // FOURTH: If restore failed, use smart fallback
        const fallback = findSmartFallbackLocal();
        if (fallback) {
          logger.debug('[useNavigationSync] Using smart fallback:', { data: fallback });
          navigateToPath(fallback.path, fallback.documentId);
        } else {
          logger.warn('[useNavigationSync] No fallback available, keeping current state');
        }
      }
    }
  }, [
    menu.length,
    activeDocumentId,
    restoreNavigation,
    findSmartFallbackLocal,
    navigateToPath,
    menuRoutes,
    findMenuPathByUrl,
  ]);

  return {
    navigateToPath,
    restoreNavigation,
    findMenuPathByUrl: (path: string) => findMenuPathByUrl(menu, path),
    findSmartFallback: findSmartFallbackLocal,
  };
};
